/**
 * Step 4 — Equipo del proyecto.
 *
 * MVP del wizard:
 *  - Internal: solo el usuario actual (auto-resuelto por /me) con un
 *    selector de role. No hay tenant-users picker — para añadir más
 *    miembros internos se usa el tab "Equipo & RACI" después de crear.
 *  - Stakeholders del cliente: lista editable email + role.
 *  - Approvals template: estándar HIVA (default) o custom-later.
 */

import { useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { CheckCircle2, Trash2, UserPlus } from 'lucide-react'

import type { MeResponse } from '../../../api/me'
import type { Template } from '../../../api/templates'
import { filterRolesByScope } from '../team/roles'
import {
  type ApprovalsTemplateKey,
  type StakeholderDraft,
  type WizardState,
} from './types'

interface Step4Props {
  state: WizardState
  me: MeResponse | undefined
  /** Catálogo de role templates (system-defaults + custom del tenant).
   * Lo fetchea el orchestrator y lo pasa para poblar el combo. */
  roles: Template[]
  onPatch: (
    patch: Partial<
      Pick<WizardState, 'internalRole' | 'approvalsTemplate'>
    >,
  ) => void
  /** Agrega un stakeholder con el rol inicial (el primero de los roles
   * de stakeholder del ciclo, o `''` si no hay). */
  onAddStakeholder: (role: string) => void
  onPatchStakeholder: (
    localId: string,
    patch: Partial<Omit<StakeholderDraft, 'localId'>>,
  ) => void
  onRemoveStakeholder: (localId: string) => void
}

export function Step4Team({
  state,
  me,
  roles,
  onPatch,
  onAddStakeholder,
  onPatchStakeholder,
  onRemoveStakeholder,
}: Step4Props) {
  const { t, i18n } = useTranslation('projects')

  const initials = me ? buildInitials(me.display_name || me.email) : '··'

  // Approval Workflow Fase 0: filtros por scope. El select del rol
  // del current user solo muestra `internal` (es el PM/sponsor que
  // crea el proyecto). El select de stakeholders solo muestra
  // `client_stakeholder`. Roles custom del tenant aparecen en uno u
  // otro según su `applicable_scopes`.
  //
  // OT-0058 — se ofrecen todos los roles internos de la metodología del
  // ciclo (`roles` ya viene acotado por `useLifecycleRoles`), no solo los
  // responsables de documentos: quien crea el proyecto puede ser, p. ej.,
  // el que aprueba. Es la misma regla que Equipo & RACI.
  const internalRoles = useMemo(
    () => filterRolesByScope(roles, 'internal', i18n.language),
    [roles, i18n.language],
  )
  const stakeholderRoles = useMemo(
    () => filterRolesByScope(roles, 'client_stakeholder', i18n.language),
    [roles, i18n.language],
  )

  // Default del rol del creador: el primero de los roles internos del
  // ciclo.
  //
  // OT-0008 D8 — ya NO se busca un rol marcado `is_project_lead`. El
  // liderazgo no sale del catálogo de roles: el backend da de alta al
  // creador como líder en el mismo `POST /projects` (OT-0065), con el rol
  // que elija aquí como `creator_role`. Ese rol es su etiqueta
  // organizacional, y elegir otra no cambia quién lidera.
  useEffect(() => {
    if (state.internalRole !== '') return
    const fallback = internalRoles[0]?.template_key
    if (fallback) {
      onPatch({ internalRole: fallback })
    }
  }, [state.internalRole, internalRoles, onPatch])

  return (
    <div className="p-6">
      <h3 className="font-display text-lg font-semibold mb-1 text-ink">
        {t('wizard.step4.title')}
      </h3>
      <p className="text-sm text-ink-muted font-body mb-5">
        {t('wizard.step4.subtitle')}
      </p>

      {/* Internal — solo el current user */}
      <p className="text-xs font-medium text-ink-muted mb-2">
        {t('wizard.step4.internal_title')}
      </p>
      <div className="grid grid-cols-[1fr_180px] gap-2 items-center mb-2">
        <div className="flex items-center gap-3 bg-canvas border border-line rounded-md px-3 py-1.5">
          <div className="w-7 h-7 rounded-full bg-brand text-canvas grid place-items-center text-xs font-semibold shrink-0">
            {initials}
          </div>
          <div className="text-sm min-w-0">
            <span className="text-ink truncate block">
              {me?.display_name ?? '—'}
            </span>
            <span className="text-ink-subtle text-xs truncate block">
              {me?.email ?? '—'}
            </span>
          </div>
        </div>
        <select
          value={state.internalRole}
          onChange={(e) => onPatch({ internalRole: e.target.value })}
          className="bg-canvas border border-line rounded-md px-2.5 py-2 text-xs"
        >
          {internalRoles.length === 0 ? (
            <option value={state.internalRole}>{state.internalRole}</option>
          ) : (
            internalRoles.map((r) => (
              <option key={r.template_key} value={r.template_key}>
                {r.name}
              </option>
            ))
          )}
        </select>
      </div>
      <p className="text-[11px] text-ink-subtle italic mb-6">
        {t('wizard.step4.internal_help')}
      </p>

      {/* Stakeholders del cliente */}
      <p className="text-xs font-medium text-ink-muted mb-2">
        {t('wizard.step4.stakeholders_title')}
      </p>
      <div className="space-y-2 mb-3">
        {state.stakeholders.map((stk) => (
          <StakeholderRow
            key={stk.localId}
            stakeholder={stk}
            roles={stakeholderRoles}
            onPatch={(patch) => onPatchStakeholder(stk.localId, patch)}
            onRemove={() => onRemoveStakeholder(stk.localId)}
          />
        ))}
      </div>
      <button
        type="button"
        onClick={() => onAddStakeholder(stakeholderRoles[0]?.template_key ?? '')}
        className="text-xs text-brand hover:underline flex items-center gap-1.5"
      >
        <UserPlus className="w-3.5 h-3.5" />
        {t('wizard.step4.add_stakeholder')}
      </button>
      <p className="text-[11px] text-ink-subtle italic mt-2">
        {t('wizard.step4.stakeholders_help')}
      </p>

      {/* Approvals template */}
      <div className="mt-6 pt-5 border-t border-line">
        <p className="text-xs font-medium text-ink-muted mb-2">
          {t('wizard.step4.approvals_title')}
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <ApprovalCard
            templateKey="standard"
            selected={state.approvalsTemplate === 'standard'}
            onSelect={() => onPatch({ approvalsTemplate: 'standard' })}
          />
          <ApprovalCard
            templateKey="custom"
            selected={state.approvalsTemplate === 'custom'}
            onSelect={() => onPatch({ approvalsTemplate: 'custom' })}
          />
        </div>
      </div>
    </div>
  )
}

function StakeholderRow({
  stakeholder,
  roles,
  onPatch,
  onRemove,
}: {
  stakeholder: StakeholderDraft
  roles: Template[]
  onPatch: (patch: Partial<Omit<StakeholderDraft, 'localId'>>) => void
  onRemove: () => void
}) {
  const { t } = useTranslation('projects')
  return (
    <div className="grid grid-cols-[1fr_200px_40px] gap-2 items-center">
      <input
        type="email"
        value={stakeholder.email}
        onChange={(e) => onPatch({ email: e.target.value })}
        placeholder={t('wizard.step4.email_placeholder')}
        className="bg-canvas border border-line rounded-md px-3 py-2 text-sm focus:outline-none focus:border-brand"
      />
      <select
        value={stakeholder.role}
        onChange={(e) => onPatch({ role: e.target.value })}
        disabled={roles.length === 0}
        className="bg-canvas border border-line rounded-md px-2.5 py-2 text-xs"
      >
        {stakeholder.role === '' ? (
          <option value="" disabled>
            {t('wizard.step4.stakeholder_role_placeholder')}
          </option>
        ) : null}
        {roles.map((r) => (
          <option key={r.template_key} value={r.template_key}>
            {r.name}
          </option>
        ))}
      </select>
      <button
        type="button"
        onClick={onRemove}
        title={t('wizard.step4.remove_member')}
        className="text-ink-subtle hover:text-critical rounded-md grid place-items-center"
      >
        <Trash2 className="w-4 h-4" />
      </button>
    </div>
  )
}

function ApprovalCard({
  templateKey,
  selected,
  onSelect,
}: {
  templateKey: ApprovalsTemplateKey
  selected: boolean
  onSelect: () => void
}) {
  const { t } = useTranslation('projects')
  return (
    <button
      type="button"
      onClick={onSelect}
      aria-pressed={selected}
      className={
        selected
          ? 'border border-brand bg-brand-soft/40 rounded-md p-3 text-left transition-colors'
          : 'border border-line rounded-md p-3 text-left hover:border-line-strong transition-colors'
      }
    >
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-ink">
          {t(`wizard.step4.approvals.${templateKey}.name` as const)}
        </span>
        {selected ? <CheckCircle2 className="w-4 h-4 text-brand" /> : null}
      </div>
      <p className="text-xs text-ink-muted mt-1 font-body">
        {t(`wizard.step4.approvals.${templateKey}.description` as const)}
      </p>
    </button>
  )
}

function buildInitials(value: string): string {
  const trimmed = value.trim()
  if (!trimmed) return '··'
  const at = trimmed.indexOf('@')
  const head = at > 0 ? trimmed.slice(0, at) : trimmed
  const parts = head.split(/[\s.\-_]+/).filter(Boolean)
  if (parts.length === 0) return '··'
  if (parts.length === 1) {
    const word = parts[0] ?? ''
    return word.slice(0, 2).toUpperCase()
  }
  const first = parts[0]?.[0] ?? ''
  const second = parts[1]?.[0] ?? ''
  return (first + second).toUpperCase()
}
